



































































































































































































































import { Component, Prop, Vue } from 'vue-property-decorator'
import { Permission, EditorialProject, App } from '@/models'
import { Utils } from '@/utils'
import { ACCOUNT_PROFILE_ADMIN } from '@/utils/constants'
import { AccountUtils } from '@/utils/account-utils'
import CopyPermissionsModal from '@/components/CopyPermissionsModal.vue'

@Component({
  components: { CopyPermissionsModal }
})
export default class AccountPermissions extends Vue {
  @Prop({ required: true }) accountId!: string
  @Prop({ required: true }) permissions!: Permission[]
  @Prop({ required: true }) apps!: App[]
  @Prop({ required: true }) editorialProjects!: EditorialProject[]
  @Prop({ default: null }) allowedEditorialProjectIds!: string[] | null

  private utils = Utils
  private ACCOUNT_PROFILE_ADMIN = ACCOUNT_PROFILE_ADMIN
  private openAccordions: { [key: string]: boolean } = {}
  private searchFilters: { [key: string]: string } = {}
  private newPermissionForms: { [key: string]: { editorialProjectIds: string[]; role: string } } = {}
  private addingPermissions: { [key: string]: boolean } = {}

  get roleOptions() {
    return [
      { value: 'READ', text: this.$t('account_detail.permissions.roles.read') },
      { value: 'EDITOR', text: this.$t('account_detail.permissions.roles.editor') },
      { value: 'SPECIAL', text: this.$t('account_detail.permissions.roles.special') }
    ]
  }

  getAppPermissions(appId: string): Permission[] {
    const appPermissions = this.permissions.filter(p => p.appId === appId)

    return appPermissions.sort((a, b) => {
      const projectA = a.editorialProjectId || ''
      const projectB = b.editorialProjectId || ''

      if (!projectA && projectB) return 1
      if (projectA && !projectB) return -1
      if (!projectA && !projectB) return 0

      const epA = this.getEditorialProject(projectA)
      const epB = this.getEditorialProject(projectB)

      if (!epA || !epB) {
        const nameA = this.getEditorialProjectName(projectA).toLowerCase()
        const nameB = this.getEditorialProjectName(projectB).toLowerCase()
        return nameA.localeCompare(nameB)
      }

      const countryA = (epA.country || '').toLowerCase()
      const countryB = (epB.country || '').toLowerCase()
      const countryCompare = countryA.localeCompare(countryB)

      if (countryCompare !== 0) {
        return countryCompare
      }

      const nameA = (epA.name || '').toLowerCase()
      const nameB = (epB.name || '').toLowerCase()
      return nameA.localeCompare(nameB)
    })
  }

  getEditorialProject(projectId: string | null): EditorialProject | null {
    if (!projectId) return null
    return this.editorialProjects.find((ep: EditorialProject) => ep.id === projectId) || null
  }

  getEditorialProjectName(projectId: string | null): string {
    if (!projectId) return 'N/A'
    const project = this.getEditorialProject(projectId)
    return project ? project.name : projectId
  }

  onAccordionShow(appId: string) {
    this.$set(this.openAccordions, appId, true)
    this.initNewPermissionForm(appId)
  }

  onAccordionHide(appId: string) {
    this.$set(this.openAccordions, appId, false)
    this.$set(this.searchFilters, appId, '')
  }

  getRoleVariant(role: string): string {
    return AccountUtils.getRoleVariant(role)
  }

  getFilteredPermissions(appId: string, permissions: Permission[]): Permission[] {
    const filter = this.searchFilters[appId]
    if (!filter || filter.trim() === '') {
      return permissions
    }
    const filterNormalized = Utils.normalizeText(filter)
    return permissions.filter(permission => {
      const projectName = this.getEditorialProjectName(permission.editorialProjectId)
      const projectNameNormalized = Utils.normalizeText(projectName)
      return projectNameNormalized.includes(filterNormalized)
    })
  }

  getAvailableEditorialProjects(appId: string): EditorialProject[] {
    const usedProjectIds = this.getAppPermissions(appId)
      .map(p => p.editorialProjectId)
      .filter(id => id)

    const available = this.editorialProjects
      .filter(ep => !usedProjectIds.includes(ep.id))
      .filter(ep => this.allowedEditorialProjectIds === null || this.allowedEditorialProjectIds.includes(ep.id))

    return available.sort((a, b) => {
      const countryA = (a.country || '').toLowerCase()
      const countryB = (b.country || '').toLowerCase()
      const countryCompare = countryA.localeCompare(countryB)

      if (countryCompare !== 0) {
        return countryCompare
      }

      const nameA = (a.name || '').toLowerCase()
      const nameB = (b.name || '').toLowerCase()
      return nameA.localeCompare(nameB)
    })
  }

  initNewPermissionForm(appId: string) {
    if (!this.newPermissionForms[appId]) {
      this.$set(this.newPermissionForms, appId, {
        editorialProjectIds: [],
        role: 'EDITOR'
      })
    }
  }

  hasAdminPermission(appId: string): boolean {
    return this.getAppPermissions(appId).some(p => p.role === 'ADMIN')
  }

  hasAccessPermission(appId: string): boolean {
    return this.getAppPermissions(appId).some(p => p.role === 'ACCESS')
  }

  handleAddPermission(appId: string) {
    const form = this.newPermissionForms[appId]
    this.$emit('add-permission', { appId, form, setAdding: (value: boolean) => this.$set(this.addingPermissions, appId, value) })
  }

  handleUpdatePermissionRole(permission: Permission, newRole: string) {
    this.$emit('update-permission-role', { permission, newRole })
  }

  handleDeletePermission(permission: Permission) {
    this.$emit('delete-permission', permission)
  }

  handleAddAdminPermission(appId: string) {
    this.$emit('add-admin-permission', appId)
  }

  handleAddAccessPermission(appId: string) {
    this.$emit('add-access-permission', appId)
  }

  handleDeleteAllPermissions(appId: string) {
    this.$emit('delete-all-permissions', appId)
  }

  showCopyPermissionsModal() {
    this.$bvModal.show('copy-permissions-modal')
  }

  clearPermissionForm(appId: string) {
    if (this.newPermissionForms[appId]) {
      this.$set(this.newPermissionForms[appId], 'editorialProjectIds', [])
    }
  }
}
